<script setup lang="ts">
/**
 * Full-card cell for the Stuff tab's mobile table: one performer's name,
 * period stat summary, and business-services breakdown — the whole "card"
 * is a single wide column, so the outer DynamicTable renders one row per
 * performer while still reading as a stacked card list on a narrow screen
 * (each `<tr>` is one card; DynamicTable's own row border/padding do the
 * separating). The breakdown itself is a nested DynamicTable (same
 * TextCell/GenderBalanceCell/MoneyCell columns as the desktop
 * PerformerStatCell equivalent, just as real columns here instead of a
 * hand-built table), matching this app's "DynamicTable everywhere, not
 * hand-rolled tables" convention throughout.
 */
import { computed, markRaw } from 'vue'
import { useI18n } from 'vue-i18n'
import DynamicTable from '@/components/table/DynamicTable.vue'
import type { Column } from '@/components/table/DynamicTable.vue'
import TextCell from '@/components/table/cell/TextCell.vue'
import MoneyCell from '@/components/table/cell/MoneyCell.vue'
import GenderBalanceCell from '@/components/table/cell/GenderBalanceCell.vue'
import StuffMobileServiceCell from '@/components/table/cell/StuffMobileServiceCell.vue'
import { majorToMinor } from '@/utils/majorToMinor'

interface LessonRow {
  dateFormatted: string
  serviceName: string
  price: number
  maleClientsCnt: number
  femaleClientsCnt: number
  groupName: string
  groupAge: string
}

defineProps<{
  /** Performer display name. */
  name: string
  /** Count of business services in the period (`stat.business_services_cnt`). */
  businessServicesCnt: number
  /** Pre-formatted total income for the period, e.g. "$1,907.00". */
  totalIncomeFormatted: string
  /** Count of distinct clients served in the period (`stat.unique_clients_cnt`). */
  uniqueClientsCnt: number
  /** One entry per business service, sorted by date, with pre-formatted date. */
  businessServices: LessonRow[]
}>()

const { t } = useI18n()

const lessonColumns = computed<Column<LessonRow>[]>(() => [
  { key: 'dateFormatted', label: t('common.date'), component: markRaw(TextCell) },
  {
    key: 'serviceName',
    label: t('common.service'),
    component: markRaw(StuffMobileServiceCell),
    props: (bs) => ({
      serviceName: bs.serviceName,
      groupName: bs.groupName,
      groupAge: bs.groupAge,
    }),
  },
  {
    key: 'balance',
    label: t('tabs.stuff.colGenderBalance'),
    component: markRaw(GenderBalanceCell),
    props: (bs) => ({ maleCnt: bs.maleClientsCnt, femaleCnt: bs.femaleClientsCnt }),
    align: 'right',
  },
  {
    key: 'price',
    label: t('common.earned'),
    component: markRaw(MoneyCell),
    props: (bs) => ({ amount: majorToMinor(bs.price) }),
    align: 'right',
  },
])
</script>

<template>
  <td class="stuff-mobile-card-cell font-tufte">
    <div class="stuff-mobile-card-cell__name">{{ name }}</div>
    <div class="stuff-mobile-card-cell__summary">
      <div class="stuff-mobile-card-cell__stat">
        <span class="stuff-mobile-card-cell__lbl">{{ t('common.lessons') }}</span>
        <span class="stuff-mobile-card-cell__val">{{ businessServicesCnt }}</span>
      </div>
      <div class="stuff-mobile-card-cell__stat">
        <span class="stuff-mobile-card-cell__lbl">{{ t('common.income') }}</span>
        <span class="stuff-mobile-card-cell__val stuff-mobile-card-cell__val--income">{{
          totalIncomeFormatted
        }}</span>
      </div>
      <div class="stuff-mobile-card-cell__stat">
        <span class="stuff-mobile-card-cell__lbl">{{ t('common.uniqueClients') }}</span>
        <span class="stuff-mobile-card-cell__val">{{ uniqueClientsCnt }}</span>
      </div>
    </div>
    <DynamicTable
      class="stuff-mobile-card-cell__lessons"
      :columns="lessonColumns as Column<unknown>[]"
      :data="businessServices"
      :pinnable-header="false"
    />
  </td>
</template>

<style scoped>
/*
 * `!important`: DynamicTable's own internal `.dynamic-table :deep(td)` rule
 * (one class + one type selector) outranks a plain `.stuff-mobile-card-cell`
 * class selector on specificity, so it would otherwise win over this
 * card-sized padding regardless of source order.
 */
.stuff-mobile-card-cell {
  vertical-align: top;
  padding: 0.85rem 1.25rem !important;
}

.stuff-mobile-card-cell__name {
  font-size: var(--text-tufte-lg);
  color: var(--color-tufte-text);
}

.stuff-mobile-card-cell__summary {
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
  margin-top: 0.6rem;
  font-family: var(--font-tufte-sans);
}

.stuff-mobile-card-cell__stat {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}

.stuff-mobile-card-cell__lbl {
  font-size: var(--text-tufte-xs);
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--color-tufte-muted);
}

.stuff-mobile-card-cell__val {
  font-family: var(--font-tufte-mono);
  font-size: var(--text-tufte-sm);
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: var(--color-tufte-text);
}

.stuff-mobile-card-cell__val--income {
  color: var(--color-money-positive);
}

.stuff-mobile-card-cell__lessons {
  margin-top: 0.6rem;
  border-top: 1px solid color-mix(in srgb, var(--color-tufte-border) 50%, transparent);
}

/*
 * :deep() overrides to compact the nested DynamicTable's default sizing —
 * same technique as FinancesTab's `.finances-table--mobile :deep(thead
 * th)` / `:deep(td)` for "DynamicTable embedded in a small mobile
 * context". Scoped through `.dynamic-table` (not just `th`/`td`) so these
 * outrank DynamicTable's own internal padding rules on specificity rather
 * than depending on CSS source order between the two components' styles.
 */
.stuff-mobile-card-cell__lessons :deep(.dynamic-table) {
  width: 100%;
  font-size: var(--text-tufte-xs);
}

.stuff-mobile-card-cell__lessons :deep(.dynamic-table th) {
  padding: 0.25rem 0.5rem 0.25rem 0;
  font-size: 0.62rem;
}

.stuff-mobile-card-cell__lessons :deep(.dynamic-table td) {
  padding: 0.3rem 0.5rem 0.3rem 0;
}
</style>
